/**
 * UI State Reducer for DataCloud component
 * Handles all UI-related state transitions
 */
import { DataCloudUIState, DataCloudUIAction, INITIAL_STEP } from '../types/dataCloud.types';

/**
 * Initial UI state
 */
export const initialUIState: DataCloudUIState = {
  currentStep: INITIAL_STEP,
  completedSteps: [],
  isNameModalOpen: false,
  saveDataName: '',
  errorMessage: null,
  errorCode: null,
  errorSuggestion: null,
  errorPersistent: false,
  successKey: null,
  successParams: null,
  isLoading: false,
  isInterpolating: false,
  showFileUpload: true
};

/**
 * UI State Reducer
 * @param state Current UI state
 * @param action Action to perform
 * @returns New UI state
 */
export const uiReducer = (
  state: DataCloudUIState,
  action: DataCloudUIAction
): DataCloudUIState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };

    case 'MARK_STEP_COMPLETED': {
      const { completedSteps } = state;
      const stepToComplete = action.payload;

      // Avoid duplicate entries
      if (completedSteps.includes(stepToComplete)) {
        return state;
      }

      return {
        ...state,
        completedSteps: [...completedSteps, stepToComplete]
      };
    }

    case 'TOGGLE_MODAL':
      return { ...state, isNameModalOpen: action.payload };

    case 'SET_SAVE_NAME':
      return { ...state, saveDataName: action.payload };

    case 'SET_ERROR':
      if (action.payload === null) {
        return {
          ...state,
          errorMessage: null,
          errorCode: null,
          errorSuggestion: null,
          errorPersistent: false,
          // Clearing an error does NOT touch success state (one-way mutex)
          successKey: state.successKey,
          successParams: state.successParams,
        };
      }
      return {
        ...state,
        errorMessage: action.payload.fallback,
        errorCode: action.payload.code ?? null,
        errorSuggestion: action.payload.suggestion ?? null,
        errorPersistent: action.payload.persistent === true,
        // Setting a real error clears any visible success toast
        successKey: null,
        successParams: null,
      };

    case 'SET_SUCCESS':
      if (action.payload === null) {
        return {
          ...state,
          successKey: null,
          successParams: null,
          // Clearing a success does NOT touch error state
        };
      }
      return {
        ...state,
        successKey: action.payload.key,
        successParams: action.payload.params ?? null,
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_INTERPOLATING':
      return { ...state, isInterpolating: action.payload };

    case 'RESET_UI':
      return { ...initialUIState };

    default: {
      // Exhaustive type checking
      const exhaustiveCheck: never = action;
      if (process.env.NODE_ENV === 'development') {
        console.warn('Unhandled action type:', exhaustiveCheck);
      }
      return state;
    }
  }
};

export default uiReducer;
